import { createFileRoute, useNavigate, redirect } from "@tanstack/react-router";
import { useState } from "react";
import { store, type CashEntryType } from "@/lib/pos-store";
import { PageHeader } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { hasRole } from "@/lib/auth-store";

export const Route = createFileRoute("/cashbook/new")({
  beforeLoad: ({ context }) => {
    if (!hasRole(context.user, "manager")) {
      throw redirect({ to: "/cashbook" });
    }
  },
  head: () => ({
    meta: [
      { title: "Record Cash Entry" },
      {
        name: "description",
        content: "Add a purchase, expenditure or debt entry.",
      },
    ],
  }),
  component: NewCash,
});

const types: { v: CashEntryType; label: string }[] = [
  { v: "purchase", label: "Purchase" },
  { v: "expenditure", label: "Expenditure" },
  { v: "debt", label: "Debt Given" },
  { v: "debt_paid", label: "Debt Paid" },
];

function NewCash() {
  const nav = useNavigate();
  const [type, setType] = useState<CashEntryType>("expenditure");
  const [desc, setDesc] = useState("");
  const [amt, setAmt] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!desc.trim()) return toast.error("Enter a description");
    const amount = parseFloat(amt);
    if (!amount || amount <= 0)
      return toast.error("Enter a valid positive amount");
    setSaving(true);
    await store.addCash(type, desc.trim(), amount);
    toast.success("Entry recorded");
    nav({ to: "/cashbook" });
  };

  return (
    <div>
      <PageHeader title="New Entry" subtitle="Money in or out" />
      <div className="space-y-4 px-4 pt-4">
        <div className="grid grid-cols-2 gap-2">
          {types.map((t) => (
            <button
              key={t.v}
              onClick={() => setType(t.v)}
              className={`rounded-xl border p-3 text-sm font-medium transition ${
                type === t.v
                  ? "border-primary bg-accent text-accent-foreground"
                  : "border-border bg-card text-muted-foreground"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div>
          <Label>Description</Label>
          <Input
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
            placeholder="e.g. Sugar from supplier"
            onKeyDown={(e) => e.key === "Enter" && submit()}
          />
        </div>
        <div>
          <Label>Amount (KES)</Label>
          <Input
            type="number"
            inputMode="numeric"
            value={amt}
            onChange={(e) => setAmt(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submit()}
          />
        </div>
        <Button className="w-full" size="lg" onClick={submit} disabled={saving}>
          {saving ? "Saving…" : "Save Entry"}
        </Button>
      </div>
    </div>
  );
}
